// CANONICAL COPY (MNSA-01, the GH #7115 pattern). src/selectors/incidentMissedNotifications.js re-exports this file so the browser and the
// Function App share one definition. Edit here, never there.
'use strict';

/**
 * src/selectors/incidentMissedNotifications.js
 *
 * Closed incidents that the statutory-notification metric (IRR-03) counts as
 * a missed notification, with nothing chasing them (GH #7393, owner decision
 * A; msr 4.6c). A framework toggle re-classifies closed incidents too, so one
 * can become notifiable after it was closed — and the hourly sweeper never
 * chases a closed incident. This derived list puts each one in front of an
 * administrator: record the (late) notification, or request a
 * re-classification. Derived from the stored record, never a second store —
 * the badge (useActionCount) and the Action Centre list read this one
 * predicate.
 */

const { isDeclassifiedAfterDeadline, honouredRequest, reclassificationsOf, isPendingRequest } = require('./incidentReclassification.cjs');

const REGIMES = Object.freeze(['gdpr', 'nis2', 'cra']);
const NOTIFICATION_STAGES = Object.freeze(['earlyWarning', 'initialNotification']);

/** The stages some recorded notification declares (a record with no `stages` predates them and may evidence any). */
function recordedStages(ob) {
  const record = ob?.submissionRecord;
  if (!record || typeof record !== 'object') return new Set();
  const { history, ...current } = record;
  const out = new Set();
  for (const v of [...(Array.isArray(history) ? history : []), current]) {
    if (!v || typeof v !== 'object') continue;
    if (Array.isArray(v.stages)) v.stages.forEach((s) => out.add(s));
    else NOTIFICATION_STAGES.forEach((s) => out.add(s));
  }
  return out;
}

/**
 * The regime's notification deadlines that passed unmet AND have no
 * notification recorded for them. Recording the late notification is what
 * the item asks for, so it clears the item — IRR-03 still counts the miss
 * (co-owner review: an item its own action cannot clear teaches admins to
 * ignore the Action Centre).
 */
function missedStages(ob, nowMs) {
  const deadlines = ob && typeof ob.deadlines === 'object' && ob.deadlines ? ob.deadlines : {};
  const met = Array.isArray(ob?.deadlinesMet) ? ob.deadlinesMet : [];
  const recorded = recordedStages(ob);
  return NOTIFICATION_STAGES.filter((name) => {
    const t = Date.parse(deadlines[name]);
    return Number.isFinite(t) && t <= nowMs && !met.includes(name) && !recorded.has(name);
  });
}

/**
 * [{ incident, regime, reason }] for administrators; [] otherwise.
 * reason: 'missed' (notifiable, a notification deadline passed unmet) or
 * 'reclassified' (re-classified after its deadline, no approved or pending
 * re-classification record).
 */
function listClosedIncidentsWithMissedNotification(state, isAdmin, nowMs = Date.now()) {
  if (!isAdmin) return [];
  const out = [];
  for (const incident of state?.incidents || []) {
    if (!incident || incident.status !== 'closed') continue;
    for (const regime of REGIMES) {
      const ob = incident.frameworkObligations?.[regime];
      if (!ob || typeof ob !== 'object') continue;
      if (ob.thresholdMet === true && missedStages(ob, nowMs).length) {
        out.push({ incident, regime, reason: 'missed' });
      } else if (
        isDeclassifiedAfterDeadline(incident, regime, nowMs) &&
        !honouredRequest(incident, regime, nowMs) &&
        !reclassificationsOf(incident, regime).some(isPendingRequest) &&
        !recordedStages(ob).has('initialNotification')
      ) {
        out.push({ incident, regime, reason: 'reclassified' });
      }
    }
  }
  return out;
}

module.exports = {
  listClosedIncidentsWithMissedNotification,
};
